/**
 * Sends a file to storage in 4 MB blocks, straight from the phone, with the
 * one-file write permission the API signed (step 7a, ruled 16 October).
 *
 * IT CARRIES ON. A block that fails - the signal drops, the phone puts the
 * page to sleep - is tried again, from that block, once the phone is online and
 * the page is showing, waiting a little longer each time. Blocks already sent
 * are not sent again. Only `stop` ends it early.
 *
 * WHAT IT CANNOT DO: send while iOS has the browser in the background or the
 * screen locked. A website gets no time there; it pauses, and this resumes when
 * the page is back. If iOS throws the page away altogether, the send is lost
 * and the video has to be chosen again (RE-33, said 16 October).
 *
 * The file only counts once the block list is committed, so a send that never
 * finishes leaves nothing the athlete can see.
 */

export const BLOCK_BYTES = 4 * 1024 * 1024;

export interface SendProgress {
  sentBytes: number;
  totalBytes: number;
  /** Waiting to try again: offline, hidden, or after a failure. */
  paused: boolean;
}

const sleep = (ms: number, signal: AbortSignal) =>
  new Promise<void>((resolve, reject) => {
    const timer = window.setTimeout(resolve, ms);
    signal.addEventListener('abort', () => {
      window.clearTimeout(timer);
      reject(signal.reason);
    }, { once: true });
  });

/** Until the phone is online and the page is in front, or the send is stopped. */
async function readyToSend(signal: AbortSignal): Promise<void> {
  while (!navigator.onLine || document.visibilityState !== 'visible') {
    await sleep(1000, signal);
  }
}

async function withRetry(
  attempt: () => Promise<Response>,
  signal: AbortSignal,
  setPaused: (paused: boolean) => void,
): Promise<void> {
  let wait = 2000;
  for (;;) {
    await readyToSend(signal);
    try {
      const response = await attempt();
      if (response.ok) {
        setPaused(false);
        return;
      }
      // A refusal that trying again cannot fix - the permission expired, or
      // was never right - ends the send.
      if (response.status === 403 || response.status === 404 || response.status === 400) {
        throw new Error(`storage refused the send: ${response.status}`);
      }
    } catch (cause) {
      if (signal.aborted) throw cause;
      if (cause instanceof Error && cause.message.startsWith('storage refused')) throw cause;
    }
    setPaused(true);
    await sleep(wait, signal);
    wait = Math.min(wait * 2, 30_000);
  }
}

const blockId = (index: number) => btoa(`block-${String(index).padStart(6, '0')}`);

export async function sendInBlocks(
  url: string,
  file: Blob,
  contentType: string,
  signal: AbortSignal,
  onProgress: (progress: SendProgress) => void,
): Promise<void> {
  const total = file.size;
  const ids: string[] = [];
  let sent = 0;
  let paused = false;
  const setPaused = (next: boolean) => {
    paused = next;
    onProgress({ sentBytes: sent, totalBytes: total, paused });
  };

  for (let index = 0, offset = 0; offset < total; index += 1, offset += BLOCK_BYTES) {
    const id = blockId(index);
    const slice = file.slice(offset, Math.min(offset + BLOCK_BYTES, total));
    await withRetry(
      () => fetch(`${url}&comp=block&blockid=${encodeURIComponent(id)}`, { method: 'PUT', body: slice, signal }),
      signal,
      setPaused,
    );
    ids.push(id);
    sent += slice.size;
    onProgress({ sentBytes: sent, totalBytes: total, paused });
  }

  const list = `<?xml version="1.0" encoding="utf-8"?><BlockList>${ids.map((id) => `<Latest>${id}</Latest>`).join('')}</BlockList>`;
  await withRetry(
    () =>
      fetch(`${url}&comp=blocklist`, {
        method: 'PUT',
        body: list,
        headers: { 'Content-Type': 'application/xml', 'x-ms-blob-content-type': contentType },
        signal,
      }),
    signal,
    setPaused,
  );
}

/** A small file in one request: the thumbnail. */
export async function sendWhole(url: string, file: Blob, contentType: string, signal: AbortSignal): Promise<void> {
  await withRetry(
    () =>
      fetch(url, {
        method: 'PUT',
        body: file,
        headers: { 'x-ms-blob-type': 'BlockBlob', 'Content-Type': contentType },
        signal,
      }),
    signal,
    () => undefined,
  );
}
